import { useEffect, useRef } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  jsonLd,
}: SEOProps) {
  const siteUrl =
    (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env
      ? ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL
      : '') || 'https://mulebuyspreadsheet.lifestyle';

  const scriptIdRef = useRef(`seo-jsonld-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    const scriptId = scriptIdRef.current;

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string, tag: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement(tag) as HTMLMetaElement;
        if (tag === 'link') {
          el.setAttribute('rel', attr);
          el.setAttribute('href', value);
        } else {
          const [attrName, attrVal] = attr.split('=');
          el.setAttribute(attrName, attrVal);
        }
        document.head.appendChild(el);
      }
      if (tag === 'link') {
        el.setAttribute('href', value);
      } else {
        el.setAttribute('content', value);
      }
    };

    setMeta('meta[name="description"]', 'name=description', description, 'meta');

    if (keywords) {
      setMeta('meta[name="keywords"]', 'name=keywords', keywords, 'meta');
    }

    const canonicalUrl = canonical || siteUrl;
    setMeta('link[rel="canonical"]', 'canonical', canonicalUrl, 'link');

    setMeta('meta[property="og:title"]', 'property=og:title', title, 'meta');
    setMeta('meta[property="og:description"]', 'property=og:description', description, 'meta');
    setMeta('meta[property="og:url"]', 'property=og:url', canonicalUrl, 'meta');
    setMeta('meta[property="og:type"]', 'property=og:type', ogType, 'meta');

    setMeta('meta[name="twitter:card"]', 'name=twitter:card', 'summary_large_image', 'meta');
    setMeta('meta[name="twitter:title"]', 'name=twitter:title', title, 'meta');
    setMeta('meta[name="twitter:description"]', 'name=twitter:description', description, 'meta');

    const lastMod = new Date().toISOString().split('T')[0];
    setMeta('meta[name="last-modified"]', 'name=last-modified', lastMod, 'meta');

    if (jsonLd) {
      const existing = document.querySelector('script[type="application/ld+json"][data-seo-dynamic]');
      if (existing) existing.remove();

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-dynamic', scriptId);
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const dynamicScript = document.querySelector(
        `script[type="application/ld+json"][data-seo-dynamic="${scriptId}"]`
      );
      if (dynamicScript) dynamicScript.remove();
    };
  }, [title, description, keywords, canonical, ogType, jsonLd, siteUrl]);

  return null;
}